import styled from "styled-components";
import { OptionsObject, Product } from "../../types";
import {
    CardActions,
    Chip,
    DialogContent,
    ImageList,
    ImageListItem,
    Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { SoecoButton } from "../../components/SoecoButton";
import Dialog from "@mui/material/Dialog";
import { useState } from "react";
import { useGetProductCategories } from "../../hooks/productCategoryHooks";
import { useGetProductColors } from "../../hooks/productColorHooks";
import { Building2, CircleDollarSign, Edit3, FileText, Image, Shapes } from "lucide-react";
import { useGetMaterials } from "../../hooks/materialHooks";

type Props = {
    product: Product;
    manufacturer: OptionsObject | null;
    designer: OptionsObject | null;
    status: OptionsObject | null;
    action: OptionsObject | null;
    material: OptionsObject | null;
    imageUrls: string[];
    step: number;
    setStep: (step: number) => void;
    moveImage: (imageUrl: string) => void;
};

export const ProductOverview = (props: Props) => {
    const [open, setOpen] = useState(false);
    const [imageModalUrl, setImageModalUrl] = useState("");

    const handleShowModal = (imageUrl: string) => {
        setImageModalUrl(imageUrl);
        setOpen(true);
    };

    const { data: productCategories } = useGetProductCategories();
    const { data: colors } = useGetProductColors();
    const { data: materials } = useGetMaterials();
    return (
        <ProductOverviewWrapper>
            {open ? (
                <Dialog open={open} onClose={() => setOpen(!open)}>
                    <DialogContent>
                        <img onClick={() => setOpen(false)} src={imageModalUrl} alt="product" />
                    </DialogContent>
                </Dialog>
            ) : null}
            {/* CameraForm */}
            <CardWrapper>
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: "30px",
                    }}
                >
                    <StyledCardActions>
                        <IconActionsWrapper>
                            {/* <CameraAltIcon color="primary" fontSize="inherit" /> */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: "10px",
                                }}
                            >
                                <Image size={24} color="black" />
                                <Typography
                                    color="#8c8c8c"
                                    fontSize={18}
                                    fontFamily={"'Poppins', sans-serif"}
                                    fontWeight={600}
                                >
                                    {props.product.name || " "}
                                </Typography>
                            </div>
                            <SoecoButton icon={true} onClick={() => props.setStep(props.step - 5)}>
                                <Edit3 size={24} color="black" />
                            </SoecoButton>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <CardContent>
                        <ImageListWrapper>
                            <ImageList cols={props.imageUrls?.length > 1 ? 2 : 1}>
                                {props.imageUrls.map(imageUrl => {
                                    return (
                                        <ImageListItem key={imageUrl}>
                                            <img
                                                onClick={() => {
                                                    props.moveImage(imageUrl);
                                                    handleShowModal(imageUrl);
                                                }}
                                                src={imageUrl}
                                                alt="image"
                                            />
                                        </ImageListItem>
                                    );
                                })}
                            </ImageList>
                        </ImageListWrapper>
                    </CardContent>
                </Card>
            </CardWrapper>

            {/* ProductCategoryForm */}
            <CardWrapper>
                <Card variant="outlined" sx={{ borderRadius: "30px" }}>
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: "10px",
                                }}
                            >
                                <Shapes size={24} color="black" />
                                <Typography
                                    color="#8c8c8c"
                                    fontSize={18}
                                    fontFamily={"'Poppins', sans-serif"}
                                    fontWeight={600}
                                >
                                    {"Product Type"}
                                </Typography>
                            </div>
                            <SoecoButton icon={true} onClick={() => props.setStep(props.step - 4)}>
                                <Edit3 size={24} color="black" />
                            </SoecoButton>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <CardContent>
                        {/* Map categories to name from productCategories */}
                        {productCategories ? (
                            productCategories.map(data => {
                                if (
                                    props.product.categories &&
                                    props.product.categories.includes(data.id)
                                ) {
                                    return (
                                        <Chip
                                            key={data.id}
                                            label={data.name}
                                            color="primary"
                                            sx={{
                                                fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                            }}
                                        />
                                    );
                                }
                            })
                        ) : (
                            <></>
                        )}
                    </CardContent>
                </Card>
            </CardWrapper>
            {/* DetailsForm */}
            <CardWrapper>
                <Card variant="outlined" sx={{ borderRadius: "30px" }}>
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: "10px",
                                }}
                            >
                                <FileText size={24} color="black" />
                                <Typography
                                    color="#8c8c8c"
                                    fontSize={18}
                                    fontFamily={"'Poppins', sans-serif"}
                                    fontWeight={600}
                                >
                                    {"Details"}
                                </Typography>
                            </div>
                            <SoecoButton icon={true} onClick={() => props.setStep(props.step - 3)}>
                                <Edit3 size={24} color="black" />
                            </SoecoButton>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <CardContent>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Name:
                            </Typography>
                            {props.product.name ? (
                                <Typography color="black" sx={fontSizeSx}>
                                    {props.product.name}
                                </Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>

                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Supplier:
                            </Typography>
                            {props.product.manufacturer ? (
                                <Typography sx={fontSizeSx}>{props.manufacturer?.label}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Designer:
                            </Typography>
                            {props.product.designer ? (
                                <Typography sx={fontSizeSx}>{props.designer?.label}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>

                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Colors:
                            </Typography>
                            {colors ? (
                                colors.map(data => {
                                    if (
                                        props.product.colors &&
                                        props.product.colors.includes(data.id)
                                    ) {
                                        return (
                                            <Chip
                                                key={data.id}
                                                label={data.name}
                                                sx={{
                                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                                }}
                                                color="primary"
                                            />
                                        );
                                    }
                                })
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        {/* RowContent */}
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Material:
                            </Typography>
                            {props.product.material ? (
                                <Typography sx={fontSizeSx}>{props.material?.label}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        {/* </CardContent> */}
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Measurements:
                            </Typography>
                            {props.product.height ||
                            props.product.width ||
                            props.product.depth ||
                            props.product.length ||
                            props.product.seatHeight ? (
                                <Typography sx={fontSizeSx}>
                                    {props.product.height ? (
                                        <Typography sx={fontSizeSx}>
                                            Height: <b>{props.product.height} mm</b>
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                    {props.product.width ? (
                                        <Typography sx={fontSizeSx}>
                                            Width: <b>{props.product.width} mm</b>
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                    {props.product.depth ? (
                                        <Typography sx={fontSizeSx}>
                                            Depth: <b>{props.product.depth} mm</b>
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                    {props.product.length ? (
                                        <Typography sx={fontSizeSx}>
                                            Length: <b>{props.product.length} mm</b>
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                    {props.product.seatHeight ? (
                                        <Typography sx={fontSizeSx}>
                                            Seating Height: <b>{props.product.seatHeight} mm</b>
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                </Typography>
                            ) : (
                                <></>
                            )}
                            {props.product.measurements ? (
                                <Typography sx={fontSizeSx}>
                                    {props.product.measurements}
                                </Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Status:
                            </Typography>
                            {props.product.status ? (
                                <Typography sx={fontSizeSx}>{props.status?.label}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Action:
                            </Typography>
                            {props.product.action ? (
                                <Typography sx={fontSizeSx}>{props.action?.label}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Address:
                            </Typography>
                            {props.product.address ? (
                                <Typography sx={fontSizeSx}>{props.product.address}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Avalible:
                            </Typography>
                            {props.product.avalible ? (
                                <Typography sx={fontSizeSx}>{props.product.avalible}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Comment:
                            </Typography>
                            {props.product.comment ? (
                                <Typography sx={fontSizeSx}>{props.product.comment}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                    </CardContent>
                </Card>
            </CardWrapper>
            {/* PriceForm */}
            <CardWrapper>
                <Card variant="outlined" sx={{ borderRadius: "30px" }}>
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: "10px",
                                }}
                            >
                                <CircleDollarSign size={24} color="black" />
                                <Typography
                                    color="#8c8c8c"
                                    fontSize={18}
                                    fontFamily={"'Poppins', sans-serif"}
                                    fontWeight={600}
                                >
                                    {"Price"}
                                </Typography>
                            </div>
                            <SoecoButton icon={true} onClick={() => props.setStep(props.step - 2)}>
                                <Edit3 size={24} color="black" />
                            </SoecoButton>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <CardContent>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Sales Price:
                            </Typography>
                            {props.product.netPrice ? (
                                <Typography sx={fontSizeSx}>{props.product.netPrice}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Purchase Price:
                            </Typography>
                            {props.product.purchasePrice ? (
                                <Typography sx={fontSizeSx}>
                                    {props.product.purchasePrice}
                                </Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                Market Price:
                            </Typography>
                            {props.product.marketPrice ? (
                                <Typography sx={fontSizeSx}>{props.product.marketPrice}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color={"#8c8c8c"} sx={fontSizeSx}>
                                CO2e (kg):
                            </Typography>
                            {props.product.co2e ? (
                                <Typography sx={fontSizeSx}>{props.product.co2e}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                    </CardContent>
                </Card>
            </CardWrapper>
            {/* QuantityLocationForm */}
            <CardWrapper>
                <Card variant="outlined" sx={{ borderRadius: "30px" }}>
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: "10px",
                                }}
                            >
                                <Building2 size={24} color="black" />
                                <Typography
                                    color="#8c8c8c"
                                    fontSize={18}
                                    fontFamily={"'Poppins', sans-serif"}
                                    fontWeight={600}
                                >
                                    {"Location"}
                                </Typography>
                            </div>
                            <SoecoButton icon={true} onClick={() => props.setStep(props.step - 1)}>
                                <Edit3 size={24} color="black" />
                            </SoecoButton>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <CardContent>
                        <RowWrapper>
                            <Typography color="#8c8c8c" sx={fontSizeSx}>
                                Town:
                            </Typography>
                            {props.product.town ? (
                                <Typography sx={fontSizeSx}>{props.product.town}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color="#8c8c8c" sx={fontSizeSx}>
                                Building:
                            </Typography>
                            {props.product.warehousePlacement ? (
                                <Typography sx={fontSizeSx}>
                                    {props.product.warehousePlacement}
                                </Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color="#8c8c8c" sx={fontSizeSx}>
                                Room:
                            </Typography>
                            {props.product.roomPlacement ? (
                                <Typography sx={fontSizeSx}>
                                    {props.product.roomPlacement}
                                </Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                        <RowWrapper>
                            <Typography color="#8c8c8c" sx={fontSizeSx}>
                                Quantity:
                            </Typography>
                            {props.product.quantity ? (
                                <Typography sx={fontSizeSx}>{props.product.quantity}</Typography>
                            ) : (
                                <></>
                            )}
                        </RowWrapper>
                    </CardContent>
                </Card>
            </CardWrapper>
        </ProductOverviewWrapper>
    );
};

const ProductOverviewWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    gap: 1rem;
`;

const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ImageListWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const RowWrapper = styled.div`
    display: flex;
    flex-direction: row;
    > p:first-of-type {
        min-width: 150px;
        text-align: left;
        color: #757575;
        font-weight: 500;
    }
    > p:last-of-type {
        flex: 1;
        word-break: break-word;
    }
`;

const IconActionsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin-right: 0.5rem;
    align-items: center;
    width: 100%;
    font-size: 2.5rem;
`;

const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
`;
const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
`;

const fontSizeSx = {
    fontSize: 16,
    fontFamily: "'Yllw Sans a1 241219', sans-serif",
    fontWeight: 400,
};
